import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { useOnReconnect } from "@/hooks/useOnReconnect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { OficinaFormDialog } from "@/components/forms/OficinaFormDialog";
import { TipoBadge } from "@/components/TipoBadge";
import { DIMENSOES } from "@/lib/constants";
import { Plus, Trash2, Dumbbell, Settings2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function Oficinas() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [oficinas, setOficinas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tipAll, setTipAll] = useState([]);
  const [tipOpen, setTipOpen] = useState(false);
  const [novaTip, setNovaTip] = useState("");
  const [editTipId, setEditTipId] = useState(null);
  const [editTipNome, setEditTipNome] = useState("");

  const load = () => api.get("/oficinas").then((r) => setOficinas(r.data));
  const refreshTip = () => api.get("/tipologias-cec").then(() => {});
  const loadTipAll = () => api.get("/tipologias-cec?incluir_inativas=true").then((r) => setTipAll(r.data));
  useEffect(() => { load(); }, []);
  useOnReconnect(() => { load(); });

  const addTip = async () => {
    if (!novaTip.trim()) return;
    await api.post("/tipologias-cec", { nome: novaTip.trim() });
    setNovaTip(""); loadTipAll(); refreshTip();
    toast.success("Tipologia adicionada");
  };
  const toggleTip = async (t) => {
    if (t.ativo) await api.delete(`/tipologias-cec/${t.id}`);
    else await api.put(`/tipologias-cec/${t.id}`, { ativo: true });
    loadTipAll();
  };
  const startEditTip = (t) => { setEditTipId(t.id); setEditTipNome(t.nome); };
  const cancelEditTip = () => { setEditTipId(null); setEditTipNome(""); };
  const saveEditTip = async (t) => {
    const nome = editTipNome.trim();
    if (!nome) return;
    await api.put(`/tipologias-cec/${t.id}`, { nome });
    cancelEditTip(); loadTipAll();
    toast.success("Tipologia atualizada");
  };

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (e, o) => { e.stopPropagation(); setEditing(o); setOpen(true); };
  const remove = async (e, id) => { e.stopPropagation(); await api.delete(`/oficinas/${id}`); toast.success("Oficina removida"); load(); };

  return (
    <div>
      <button data-testid="oficinas-voltar" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
        <span aria-hidden>←</span> Voltar
      </button>
      <PageHeader title="Oficinas" subtitle="Atividades e suas fichas de critérios">
        {isAdmin && (
          <div className="flex gap-2">
            <Dialog open={tipOpen} onOpenChange={(v) => { setTipOpen(v); if (v) loadTipAll(); }}>
              <DialogTrigger asChild><Button variant="secondary" data-testid="gerenciar-tipologias-btn"><Settings2 className="h-4 w-4 mr-2" />Tipologias CEC</Button></DialogTrigger>
              <DialogContent className="bg-popover border-border">
                <DialogHeader><DialogTitle>Tipologias do CEC</DialogTitle></DialogHeader>
                <div className="flex gap-2">
                  <Input data-testid="nova-tipologia-input" placeholder="Nova tipologia" value={novaTip} onChange={(e) => setNovaTip(e.target.value)} />
                  <Button data-testid="add-tipologia-btn" onClick={addTip} disabled={!novaTip.trim()}>Adicionar</Button>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {tipAll.map((t) => (
                    <div key={t.id} data-testid={`tip-row-${t.id}`} className="flex items-center justify-between gap-2 border border-border rounded-md p-2.5">
                      {editTipId === t.id ? (
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Input data-testid={`tip-edit-input-${t.id}`} value={editTipNome} onChange={(e) => setEditTipNome(e.target.value)} className="h-8" />
                          <button data-testid={`tip-edit-save-${t.id}`} onClick={() => saveEditTip(t)} className="text-emerald-400 hover:text-emerald-300"><Check className="h-4 w-4" /></button>
                          <button data-testid={`tip-edit-cancel-${t.id}`} onClick={cancelEditTip} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                        </div>
                      ) : (
                        <>
                          <span className={t.ativo ? "" : "line-through text-muted-foreground"}>{t.nome}</span>
                          <div className="flex items-center gap-2">
                            <button data-testid={`tip-edit-${t.id}`} onClick={() => startEditTip(t)} className="text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                            <Badge className={t.ativo ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-slate-500/20 text-slate-300 border-slate-500/40"}>{t.ativo ? "Ativa" : "Inativa"}</Badge>
                            <Switch data-testid={`tip-toggle-${t.id}`} checked={t.ativo} onCheckedChange={() => toggleTip(t)} />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {!tipAll.length && <p className="text-sm text-muted-foreground">Nenhuma tipologia cadastrada.</p>}
                </div>
              </DialogContent>
            </Dialog>
            <Button data-testid="nova-oficina-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" />Nova oficina</Button>
          </div>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {oficinas.map((o) => {
          const grupos = [...new Set((o.criterios || []).map((c) => c.grupo).filter(Boolean))];
          return (
            <Card key={o.id} data-testid={`oficina-${o.id}`} onClick={() => navigate(`/oficinas/${o.id}`)}
              className="p-5 bg-card border-border cursor-pointer hover:border-primary/60 hover:bg-card/70 transition-all">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-emerald-500/15"><Dumbbell className="h-5 w-5 text-emerald-400" /></div>
                  <div className="min-w-0">
                    <div className="font-semibold text-lg leading-tight">{o.nome}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                      <TipoBadge tipo={o.curso_tipo} className="shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">{o.curso_nome}{o.modulo ? ` · ${o.modulo}` : ""}</span>
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`edit-oficina-${o.id}`} onClick={(e) => openEdit(e, o)} className="text-muted-foreground hover:text-primary"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`del-oficina-${o.id}`} onClick={(e) => remove(e, o.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              {o.objetivo && <p className="text-sm text-muted-foreground mt-3">{o.objetivo}</p>}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {o.tipologia_cec_nome && <Badge className="bg-primary/15 text-primary border-primary/30" data-testid={`oficina-tip-${o.id}`}>CEC: {o.tipologia_cec_nome}</Badge>}
                <span className="text-xs text-muted-foreground">{(o.criterios || []).length} critérios</span>
                {grupos.length > 0 && <span className="text-xs text-muted-foreground">· {grupos.length} grupo(s)</span>}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(o.criterios || []).slice(0, 8).map((c, i) => (
                  <Badge key={i} className={`${DIMENSOES[c.dimensao]?.badge} whitespace-normal break-words max-w-full text-left`}>{c.nome}</Badge>
                ))}
                {(o.criterios || []).length > 8 && <Badge className="bg-secondary text-muted-foreground">+{o.criterios.length - 8}</Badge>}
              </div>
            </Card>
          );
        })}
        {!oficinas.length && <p className="text-muted-foreground text-sm">Nenhuma oficina cadastrada.</p>}
      </div>

      <OficinaFormDialog open={open} onOpenChange={setOpen} oficina={editing} onSaved={load} />
    </div>
  );
}
